import React, { useState, useRef, useEffect, useMemo } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { useStats } from '../../hooks/useStats';
import { formatInnings } from '../../utils/formatters';
import { formatPitchingStat, ensureNumber } from '../../utils/calculations';
import StatCard from '../shared/StatCard';
import { Clock, TrendingUp, TrendingDown, Target, HelpCircle, RefreshCw, Filter } from 'lucide-react';
import { filterGames, calculateFilteredStats } from '../../utils/statsFilters';

const PitchingStats = ({ games = [], filters = {} }) => {
  const { playerData, activeSeason, viewingAllSeasons, selectedPlayerId, dataVersion } = usePlayer();
  const [showTooltip, setShowTooltip] = useState(null);

  // Use the useStats hook to get pitching stats
  const {
    pitchingStats: hookPitchingStats,
    loading,
    error,
    dataSource,
    refreshStats
  } = useStats(selectedPlayerId, activeSeason, viewingAllSeasons, dataVersion, playerData);
  
  // Filter games based on filter criteria
  const filteredGames = useMemo(() => {
    return filterGames(games, filters);
  }, [games, filters]);

  // Calculate stats from filtered games if filters are applied
  const pitchingStats = useMemo(() => {
    // Check if any filters are applied (except dateRange='all' which is the default)
    const hasActiveFilters = 
      filters.gameId || 
      filters.dateRange !== 'all' || 
      filters.opponent !== 'all' || 
      filters.location !== 'all';

    if (hasActiveFilters && filteredGames.length > 0) {
      // Calculate stats from filtered games
      const calculatePitchingStatsFromGames = (games) => {
        // Extract pitching stats from each game
        const pitchingData = games.reduce((acc, game) => {
          if (game.pitchingStats) {
            // Add each stat from the game to the accumulator
            Object.entries(game.pitchingStats).forEach(([key, value]) => {
              // Special handling for inningsPitched to account for fractional innings
              if (key === 'inningsPitched') {
                acc[key] = (acc[key] || 0) + Number(value || 0);
              } else {
                acc[key] = (acc[key] || 0) + Number(value || 0);
              }
            });
          }
          return acc;
        }, {});

        // Calculate ERA and WHIP
        const inningsPitched = ensureNumber(pitchingData.inningsPitched);
        const earnedRuns = ensureNumber(pitchingData.earnedRuns);
        const walks = ensureNumber(pitchingData.walks);
        const hits = ensureNumber(pitchingData.hits);

        if (inningsPitched > 0) {
          pitchingData.era = (earnedRuns * 9) / inningsPitched;
          pitchingData.whip = (walks + hits) / inningsPitched;
        } else {
          pitchingData.era = 0;
          pitchingData.whip = 0;
        }

        return pitchingData;
      };

      return calculateFilteredStats(
        filteredGames, 
        calculatePitchingStatsFromGames, 
        hookPitchingStats
      );
    }

    // If no filters are applied, use the stats from the hook
    return hookPitchingStats;
  }, [hookPitchingStats, filteredGames, filters]);

  // Show active filter indicator
  const hasActiveFilters = 
    filters.gameId || 
    filters.dateRange !== 'all' || 
    filters.opponent !== 'all' || 
    filters.location !== 'all';
  
  // Check if there are any pitching stats to display
  const hasPitchingStats = pitchingStats && (
    pitchingStats.inningsPitched > 0 || 
    pitchingStats.games > 0 || 
    pitchingStats.strikeouts > 0 ||
    pitchingStats.wins > 0 ||
    pitchingStats.earnedRuns > 0
  );
  
  // Add debounce for stats refresh
  const refreshTimerRef = useRef(null);
  
  // Effect to fetch stats on mount and when dependencies change, with debounce
  useEffect(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Set a new timer to delay the refresh
    refreshTimerRef.current = setTimeout(() => {
      console.log('Pitching stats refresh triggered due to dependency change (debounced):', {
        playerId: selectedPlayerId,
        seasonId: activeSeason?.id,
        viewingAllSeasons
      });
      
      // Use the cached stats if available instead of forcing a refresh
      // This prevents too many Firestore requests
    }, 1000); // 1 second debounce
    
    // Cleanup function
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [selectedPlayerId, activeSeason, viewingAllSeasons]);
  
  // Handle manual refresh click
  const handleRefreshClick = () => {
    refreshStats();
  };
  
  // Tooltip content for stats
  const tooltips = {
    era: "Earned Run Average: (Earned Runs × 9) / Innings Pitched. Lower is better.",
    whip: "Walks plus Hits per Inning Pitched: (Walks + Hits) / Innings Pitched. Lower is better.",
    k9: "Strikeouts per 9 innings: (Strikeouts × 9) / Innings Pitched. Higher is better.",
    strike_rate: "Percentage of pitches thrown for strikes."
  };
  
  // Tooltip component
  const Tooltip = ({ id, content }) => (
    <div 
      className="absolute z-10 bg-gray-800 dark:bg-dark-400 text-white p-2 rounded shadow-lg text-xs max-w-xs"
      style={{ bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' }}
    >
      {content}
      <div 
        className="absolute w-2 h-2 bg-gray-800 dark:bg-dark-400 transform rotate-45"
        style={{ bottom: '-4px', left: '50%', marginLeft: '-4px' }}
      ></div>
    </div>
  );

  // Utility to calculate mock trend data
  const calculateTrend = (stat) => {
    // In a real app, this would compare recent performance to past averages
    // For demo purposes, we'll generate a random trend between -25 and 25
    const trend = Math.floor(Math.random() * 51) - 25;
    return trend;
  };

  // Trend explanations
  const getTrendExplanation = (stat, trend) => {
    const direction = trend > 0 ? 'up' : 'down';
    const games = '5'; // In a real app, this would be dynamic
    
    switch(stat) {
      case 'era':
        return `ERA is ${direction} ${Math.abs(trend)}% from last ${games} games`;
      case 'whip':
        return `WHIP is ${direction} ${Math.abs(trend)}% compared to season average`;
      case 'k9':
        return `K/9 is ${direction} ${Math.abs(trend)}% from previous month`;
      case 'strike_rate':
        return `Strike rate is ${direction} ${Math.abs(trend)}% from your career average`;
      default:
        return `${direction} ${Math.abs(trend)}% from average`;
    }
  };

  // Determine if a trend is good or bad
  const isTrendPositive = (stat, trend) => {
    // For ERA and WHIP, down is good
    if (stat === 'era' || stat === 'whip') {
      return trend < 0;
    }
    // For K/9 and strike rate, up is good
    return trend > 0;
  };
  
  // Calculate K/9 (strikeouts per 9 innings)
  const calculateK9 = () => {
    const inningsPitched = ensureNumber(pitchingStats.inningsPitched);
    const strikeouts = ensureNumber(pitchingStats.strikeouts);
    
    if (inningsPitched <= 0) return '0.00';
    return formatPitchingStat((strikeouts * 9) / inningsPitched);
  };

  // Stat item with tooltip support
  const StatItem = ({ label, value, tooltip }) => (
    <div className="relative flex justify-between items-center mb-2 pb-2 border-b border-gray-100 dark:border-dark-300">
      <div className="flex items-center">
        <span>{label}</span>
        {tooltip && (
          <div className="relative ml-1">
            <HelpCircle 
              size={14} 
              className="text-gray-400 dark:text-gray-500 cursor-help" 
              onMouseEnter={() => setShowTooltip(label)} 
              onMouseLeave={() => setShowTooltip(null)} 
            />
            {showTooltip === label && <Tooltip id={label} content={tooltip} />}
          </div>
        )}
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );

  // Stat item with trend indicator
  const StatItemWithTrend = ({ label, value, tooltip, statKey }) => {
    const trend = calculateTrend(statKey);
    const isPositive = isTrendPositive(statKey, trend);
    const trendExplanation = getTrendExplanation(statKey, trend);

    return (
      <div className="relative flex justify-between items-center mb-2 pb-2 border-b border-gray-100 dark:border-dark-300">
        <div className="flex items-center">
          <span>{label}</span>
          {tooltip && (
            <div className="relative ml-1">
              <HelpCircle 
                size={14} 
                className="text-gray-400 dark:text-gray-500 cursor-help" 
                onMouseEnter={() => setShowTooltip(label)} 
                onMouseLeave={() => setShowTooltip(null)} 
              />
              {showTooltip === label && <Tooltip id={label} content={tooltip} />}
            </div>
          )}
        </div>
        <div className="flex items-center">
          <div 
            className="relative mr-2 cursor-help"
            onMouseEnter={() => setShowTooltip(`trend-${statKey}`)} 
            onMouseLeave={() => setShowTooltip(null)}
          >
            {isPositive ? (
              <TrendingUp size={16} className="text-green-500 dark:text-green-400" />
            ) : (
              <TrendingDown size={16} className="text-red-500 dark:text-red-400" />
            )}
            {showTooltip === `trend-${statKey}` && (
              <Tooltip id={`trend-${statKey}`} content={trendExplanation} />
            )}
          </div>
          <span className="font-semibold">{value}</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-bold dark:text-gray-100">Pitching Statistics</h2>
          
          {/* Show filter indicator if filters are applied */}
          {hasActiveFilters && (
            <div className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full flex items-center">
              <Filter size={12} className="mr-1" />
              <span>Filtered</span>
            </div>
          )}
        </div>
        
        {/* Refresh Button */}
        <button 
          onClick={handleRefreshClick} 
          className="flex items-center px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 dark:text-blue-300 rounded-md transition-colors"
          disabled={loading}
          title="Refresh statistics"
        >
          <RefreshCw size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm">Refresh Stats</span>
        </button>
      </div>
      
      {/* Loading indicator */}
      {loading && (
        <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30 rounded-md text-sm">
          <div className="flex items-center">
            <RefreshCw size={14} className="animate-spin mr-2" />
            <span>Refreshing statistics...</span>
          </div>
        </div>
      )}
      
      {/* Display stats context information if filters are applied */}
      {hasActiveFilters && (
        <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30 rounded-md text-sm">
          <div className="flex items-center">
            <Filter size={14} className="mr-1" />
            <span>
              Showing {filteredGames.length} of {games.length} games
            </span>
          </div>
        </div>
      )}
      
      {hasPitchingStats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Overview Card */}
          <StatCard
            title="Overview"
            icon={Clock}
            className="h-full"
          >
            <div className="space-y-1">
              <StatItem 
                label="Games" 
                value={pitchingStats.games || 0} 
              />
              <StatItem 
                label="Record (W-L)" 
                value={`${pitchingStats.wins || 0}-${pitchingStats.losses || 0}`} 
              />
              <StatItem 
                label="Innings Pitched (IP)" 
                value={formatInnings(pitchingStats.inningsPitched)} 
                tooltip="Total innings pitched, with partial innings in decimal form"
              />
            </div>
          </StatCard>
          
          {/* Rate Stats Card */}
          <StatCard
            title="Rate Stats"
            icon={TrendingUp}
            className="h-full"
          >
            <div className="space-y-1">
              <StatItemWithTrend 
                label="Earned Run Average (ERA)" 
                value={formatPitchingStat(pitchingStats.era)} 
                tooltip={tooltips.era}
                statKey="era"
              />
              <StatItemWithTrend 
                label="WHIP" 
                value={formatPitchingStat(pitchingStats.whip)} 
                tooltip={tooltips.whip}
                statKey="whip"
              />
              <StatItemWithTrend 
                label="K/9" 
                value={calculateK9()} 
                tooltip={tooltips.k9}
                statKey="k9"
              />
            </div>
          </StatCard>
          
          {/* Additional Stats Card */}
          <StatCard
            title="Additional Stats"
            icon={Target}
            className="h-full"
          >
            <div className="space-y-1">
              <StatItem 
                label="Strikeouts (K)" 
                value={Number(pitchingStats.strikeouts) || 0} 
              />
              <StatItem 
                label="Walks (BB)" 
                value={Number(pitchingStats.walks) || 0} 
              />
              <StatItem 
                label="Hits Allowed" 
                value={Number(pitchingStats.hits) || 0} 
              />
              <StatItem 
                label="Earned Runs" 
                value={Number(pitchingStats.earnedRuns) || 0} 
              />
            </div>
          </StatCard>
        </div>
      ) : (
        <div className="p-6 text-center bg-gray-50 dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-400">
          <p className="text-gray-600 dark:text-darkText-secondary mb-2">No pitching statistics available</p>
          <p className="text-sm text-gray-500 dark:text-darkText-tertiary">
            {activeSeason && !viewingAllSeasons 
              ? `No pitching data recorded for the ${activeSeason.name} season.` 
              : "No career pitching statistics available."}
          </p>
        </div>
      )}
    </div>
  );
};

export default PitchingStats;
